import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import type {
  CursorPage,
  ListMediaParams,
  MediaFacets,
  MediaItem,
  UpdateMediaBody,
} from '@/types/api';

/**
 * Images and videos, newest first, a cursor page at a time — the same contract as the catalogue's
 * links. The presigned URLs last about an hour; pages are refetched well before that, after
 * fifteen minutes, so a video opened from a page left idle still plays to the end.
 */
export function useMediaListQuery(
  params: ListMediaParams,
): UseInfiniteQueryResult<{ pages: CursorPage<MediaItem>[] }, Error> {
  return useInfiniteQuery({
    queryKey: queryKeys.mediaList(params),
    queryFn: ({ pageParam, signal }) =>
      apiClient.listMedia({ ...params, cursor: pageParam }, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage: CursorPage<MediaItem>) => lastPage.nextCursor ?? undefined,
    staleTime: 15 * 60 * 1000,
  });
}

/** Channel and sharer options for the Media page's filters. */
export function useMediaFacetsQuery(): UseQueryResult<MediaFacets, Error> {
  return useQuery<MediaFacets, Error>({
    queryKey: queryKeys.mediaFacets(),
    queryFn: ({ signal }) => apiClient.getMediaFacets(signal),
    staleTime: 5 * 60 * 1000,
  });
}

export function useDeleteMedia() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (mediaId) => apiClient.deleteMedia(mediaId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.media() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.stats() });
    },
  });
}

export function useUpdateMedia() {
  const queryClient = useQueryClient();
  return useMutation<MediaItem, Error, { mediaId: string; body: UpdateMediaBody }>({
    mutationFn: ({ mediaId, body }) => apiClient.updateMedia(mediaId, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.media() }),
  });
}

/** When to look for the new text after asking for it: a still image takes seconds, a video minutes. */
const REDESCRIBE_REFRESH_AFTER_MS = [20_000, 90_000, 240_000];

/**
 * Asks for a new generated title and description. The describer works in the background, so the
 * list is refreshed a few times afterwards instead of the answer being awaited.
 */
export function useRegenerateMediaText() {
  const queryClient = useQueryClient();
  return useMutation<{ queued: boolean }, Error, string>({
    mutationFn: (mediaId) => apiClient.describeMedia(mediaId),
    onSuccess: () => {
      for (const delay of REDESCRIBE_REFRESH_AFTER_MS) {
        setTimeout(
          () => void queryClient.invalidateQueries({ queryKey: queryKeys.media() }),
          delay,
        );
      }
    },
  });
}
